import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ColumnConfigState {
  visibleColumns: string[];
  columnOrder: string[];
  pinnedColumns: string[];
  _hasHydrated: boolean;

  setVisibleColumns: (columns: string[]) => void;
  setColumnOrder: (order: string[]) => void;
  setPinnedColumns: (columns: string[]) => void;
  toggleColumnVisibility: (columnKey: string) => void;
  toggleColumnPin: (columnKey: string) => void;
  resetToDefaults: (defaultColumns: string[]) => void;
  setHasHydrated: (state: boolean) => void;
}

/**
 * Factory to create entity-specific column config stores with localStorage persistence
 * @param entityName - Used for localStorage key (e.g., 'contact', 'sequence')
 * @param defaultColumns - Default column keys when store is empty
 */
export function createColumnConfigStore(entityName: string, defaultColumns: string[] = []) {
  const store = create<ColumnConfigState>()(
    persist(
      (set) => ({
        visibleColumns: defaultColumns,
        columnOrder: defaultColumns,
        pinnedColumns: [],
        _hasHydrated: false,

        setVisibleColumns: (columns) => set({ visibleColumns: columns }),
        setColumnOrder: (order) => set({ columnOrder: order }),
        setPinnedColumns: (columns) => set({ pinnedColumns: columns }),

        toggleColumnVisibility: (columnKey) =>
          set((state) => {
            const visible = state.visibleColumns.includes(columnKey);
            return {
              visibleColumns: visible
                ? state.visibleColumns.filter((k) => k !== columnKey)
                : [...state.visibleColumns, columnKey],
            };
          }),

        toggleColumnPin: (columnKey) =>
          set((state) => {
            const pinned = state.pinnedColumns.includes(columnKey);
            return {
              pinnedColumns: pinned
                ? state.pinnedColumns.filter((k) => k !== columnKey)
                : [...state.pinnedColumns, columnKey],
            };
          }),

        resetToDefaults: (defaultCols) =>
          set({
            visibleColumns: defaultCols,
            columnOrder: defaultCols,
            pinnedColumns: [],
          }),

        setHasHydrated: (state) => set({ _hasHydrated: state }),
      }),
      {
        name: `${entityName}-column-config-v3`,
        onRehydrateStorage: () => (state) => {
          // Mark hydration as complete
          state?.setHasHydrated(true);

          if (!state) return;

          // If hydrated state has empty columns, reset to defaults
          if (state.columnOrder.length === 0 || state.visibleColumns.length === 0) {
            state.resetToDefaults(defaultColumns);
            return;
          }

          // Merge in any default columns that weren't in the persisted state
          // (i.e. keys added after the user's last save). Appended as visible so
          // users discover them; explicit hides of already-known keys are kept.
          const missing = defaultColumns.filter((key) => !state.columnOrder.includes(key));
          if (missing.length > 0) {
            state.setColumnOrder([...state.columnOrder, ...missing]);
            state.setVisibleColumns([...state.visibleColumns, ...missing]);
          }
        },
        partialize: (state) => ({
          visibleColumns: state.visibleColumns,
          columnOrder: state.columnOrder,
          pinnedColumns: state.pinnedColumns,
        }),
      },
    ),
  );

  return store;
}
